
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import {settings} from '@/plugins/settings';
import {IHelpdeskTicket, ILinkedUser} from '@/store/modules/helpdeskTicketsModule';
import {findUsersByEmail, IMayordomoUser, searchMayordomoUsers, USER_SEARCH_MIN_LENGTH} from '@/services/helpdeskUserLookup';
import {ticketThreadParticipants} from '@/services/helpdeskThreadParticipants';

interface ILinkedRow {
  userId: number;
  name: string;
  email: string;
  fromThread: boolean;
  manual: ILinkedUser | null;
}

@Component({name: 'HelpdeskLinkedUsersTab'})
export default class HelpdeskLinkedUsersTab extends Vue {
  @Prop({required: true}) ticket!: IHelpdeskTicket;

  localLinkedUsers: ILinkedUser[] = [];
  threadUsers: IMayordomoUser[] = [];
  detecting        = false;
  searchQuery      = '';
  searchResults: IMayordomoUser[] = [];
  searching        = false;
  searchDone       = false;
  linking: number | null   = null;
  unlinking: number | null = null;
  private searchTimer: any = null;
  private detectRun = 0;

  get ticketId(): number {
    return this.ticket.id;
  }

  @Watch('ticketId')
  onTicketIdChanged() {
    this.searchQuery   = '';
    this.searchResults = [];
    this.searchDone    = false;
  }

  @Watch('ticket.linked_users', {immediate: true})
  onLinkedUsersChanged(val: ILinkedUser[] | undefined) {
    this.localLinkedUsers = val ? [...val] : [];
  }

  // Re-resolve only when the conversation actually changes, not on every ticket object refresh.
  get threadKey(): string {
    const t = this.ticket;
    const ids = (t.emails || []).map((e) => e.id).join(',');
    const sent = (t.sent_emails || []).map((s) => s.id).join(',');
    return t.id + '|' + (t.reporter_email || '') + '|' + ids + '|' + sent;
  }

  @Watch('threadKey', {immediate: true})
  onThreadChanged() {
    // Guards against a slower lookup from a previous ticket overwriting the current one.
    const run = ++this.detectRun;
    this.threadUsers = [];
    this.detecting = true;
    ticketThreadParticipants(this.ticket)
      .then((emails) => Promise.all(emails.map((e) => findUsersByEmail(e))))
      .then((lists) => {
        if (run !== this.detectRun) return;
        const byId: {[id: number]: IMayordomoUser} = {};
        lists.forEach((users) => users.forEach((u) => { byId[u.id] = u; }));
        this.threadUsers = Object.keys(byId).map((k) => byId[Number(k)]);
        this.detecting = false;
      });
  }

  get rows(): ILinkedRow[] {
    const byId: {[id: number]: ILinkedRow} = {};
    const order: number[] = [];
    this.threadUsers.forEach((u) => {
      byId[u.id] = {
        userId: u.id,
        name: ((u.first_name || '') + ' ' + (u.last_name || '')).trim() || u.email,
        email: u.email,
        fromThread: true,
        manual: null,
      };
      order.push(u.id);
    });
    this.localLinkedUsers.forEach((lu) => {
      if (byId[lu.user_id]) {
        byId[lu.user_id].manual = lu;
      } else {
        byId[lu.user_id] = {userId: lu.user_id, name: lu.user_name, email: lu.user_email, fromThread: false, manual: lu};
        order.push(lu.user_id);
      }
    });
    return order.map((id) => byId[id]);
  }

  @Watch('rows')
  onRowsChanged(val: ILinkedRow[]) {
    this.$emit('count', val.length);
  }

  // Resolved via the router so the link works wherever the SPA is hosted (bucket root or /modulo/).
  userHref(userId: number): string {
    return this.$router.resolve({name: 'user', params: {userId: String(userId)}}).href;
  }

  initialOf(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  isAlreadyLinked(userId: number): boolean {
    return this.rows.some((row) => row.userId === userId);
  }

  onSearchInput() {
    clearTimeout(this.searchTimer);
    if (!this.searchQuery || this.searchQuery.trim().length < USER_SEARCH_MIN_LENGTH) {
      this.searchResults = [];
      this.searchDone    = false;
      return;
    }
    this.searchTimer = setTimeout(() => { this.doSearch(); }, 350);
  }

  clearSearch() {
    this.searchQuery   = '';
    this.searchResults = [];
    this.searchDone    = false;
  }

  private doSearch() {
    const query = this.searchQuery;
    this.searching = true;
    this.searchDone = false;
    searchMayordomoUsers(query)
      .catch(() => [] as IMayordomoUser[])
      .then((users) => {
        // A slower response for an earlier query must not replace the current results.
        if (query !== this.searchQuery) return;
        this.searchResults = users;
        this.searchDone    = true;
        this.searching     = false;
      });
  }

  linkUser(u: IMayordomoUser) {
    if (this.linking !== null) return;
    this.linking = u.id;

    const userName  = ((u.first_name || '') + ' ' + (u.last_name || '')).trim() || u.email;
    const userEmail = u.email;

    helpdeskAxios({
      url:    settings.helpdeskEndpoint + 'v1/tickets/' + this.ticketId + '/linked-users',
      method: 'POST',
      data:   {user_id: u.id, user_name: userName, user_email: userEmail},
    })
      .then((res: any) => {
        const created: ILinkedUser = res.data && res.data.data ? res.data.data : {
          id: 0, ticket_id: this.ticketId, user_id: u.id, user_name: userName, user_email: userEmail,
        };
        this.localLinkedUsers.push(created);
        this.linking = null;
      })
      .catch(() => { this.linking = null; });
  }

  unlinkUser(lu: ILinkedUser) {
    if (this.unlinking !== null) return;
    this.unlinking = lu.id;

    helpdeskAxios({
      url:    settings.helpdeskEndpoint + 'v1/tickets/' + this.ticketId + '/linked-users/' + lu.user_id,
      method: 'DELETE',
    })
      .then(() => {
        const idx = this.localLinkedUsers.indexOf(lu);
        if (idx !== -1) this.localLinkedUsers.splice(idx, 1);
        this.unlinking = null;
      })
      .catch(() => { this.unlinking = null; });
  }
}
